<script setup lang="ts">
// Forked from payments-mfe src/components/modals/PlaidConnectModal.vue (PLAT-2857), adapted for
// the Collect flow: the link token is injected (see plaid.service.ts fork note), and the outcome
// is emitted to the caller — the Collect request is already complete before this modal opens, so
// this component performs no store writes of its own.
import { computed, onMounted } from 'vue';
import { PlaidService } from '@Services/plaid.service';
import {
    parsePlaidLinkExitActionStatus,
    type PlaidInstitution,
    type PlaidLinkExitAction,
    type PlaidLinkExitActionError,
    type PlaidLinkExitActionMetadata,
    type PlaidLinkOnSuccessMetadata
} from '@Types/plaid';

const props = defineProps<{
    modelValue: boolean,
    linkToken: string
}>();
const emit = defineEmits<{
    (e: 'update:modelValue', value: boolean): void,
    (e: 'on-success', publicToken: string, metadata: PlaidLinkOnSuccessMetadata): void,
    (e: 'on-exit', isUserExit: boolean, exitAction: PlaidLinkExitAction): void
}>();

const plaidService = new PlaidService();

const isModalOpen = computed({
    get: () => props.modelValue,
    set: (value: boolean) => emit('update:modelValue', value)
});

// Plaid handling — builders copied verbatim from the payments-mfe original.
const buildPlaidLinkExitAction = (err: any, metadata: any): PlaidLinkExitAction => {
    const plaidLinkExitActionError = buildPlaidLinkExitActionError(err);
    const plaidLinkExitActionMetadata = buildPlaidLinkExitActionMetadata(metadata);

    return {
        error: plaidLinkExitActionError,
        metadata: plaidLinkExitActionMetadata,
    }
};

const buildPlaidLinkExitActionError = (err: any): PlaidLinkExitActionError | null => {
    if (err === null || err === undefined) {
        return null;
    }

    return {
        errorType: err.error_type,
        errorCode: err.error_code,
        errorMessage: err.error_message,
        displayMessage: err.display_message,
    };
};

const buildPlaidLinkExitActionMetadata = (metadata: any): PlaidLinkExitActionMetadata => {
    const plaidInstitution = buildPlaidInstitution(metadata?.institution);
    const plaidLinkExitActionStatus = parsePlaidLinkExitActionStatus(metadata?.status);

    return {
        institution: plaidInstitution,
        status: plaidLinkExitActionStatus,
        linkSessionId: metadata?.link_session_id,
        requestId: metadata?.request_id,
    }
};

const buildPlaidInstitution = (institution: any | null): PlaidInstitution | null => {
    if (institution === null || institution === undefined) {
        return null;
    }

    return {
        institutionId: institution.institution_id,
        name: institution.name,
    };
};

// Lifecycle hooks
onMounted(async () => {
    try {
        await plaidService.init(props.linkToken);

        plaidService.openPlaidLink(
            (publicToken, metadata) => {
                isModalOpen.value = false;
                emit('on-success', publicToken, metadata);
            },
            (err, metadata) => {
                const isUserExit = err === null || err === undefined;
                const exitAction = buildPlaidLinkExitAction(err, metadata);
                isModalOpen.value = false;
                emit('on-exit', isUserExit, exitAction);
            }
        );
    } catch (err) {
        // Script load / Link init failure (ad-blocker, network): without this the full-screen
        // overlay would stay up forever. The request is already complete — close and report a
        // failed attempt so the flow advances to the success screen.
        console.error('[PlaidConnectModal] Plaid Link failed to initialize:', err);
        isModalOpen.value = false;
        emit('on-exit', false, {
            error: {
                errorType: 'PLAID_INIT_FAILED',
                errorCode: 'PLAID_INIT_FAILED',
                errorMessage: err instanceof Error ? err.message : String(err),
                displayMessage: null,
            },
            metadata: { institution: null, status: 'UnknownStatus', linkSessionId: '', requestId: '' },
        });
    }
});
</script>

<template>
    <div class="plaid-connect-modal" />
</template>

<style lang="scss" scoped>
@use 'certifid-v2-client-hub-styles' as *;

.plaid-connect-modal {
    display: flex;
    justify-content: center;
    align-items: center;
    height: 100vh;
    width: 100vw;
    position: fixed;
    top: 0;
    left: 0;
    background-color: color-mix(in srgb, $theme-primary-dark 86%, transparent);
    z-index: 100;
}
</style>
